<template>
  <div>
    <div
      v-if="hasApiKeys"
      class="d-flex justify-content-between align-items-center i-mb-14"
    >
      <div>
        <settings-section-title id="api" class="i-mb-2"
          >API Keys</settings-section-title
        >

        <p class="i-text-body-2 i-text-neutral-110 i-mr-6">
          Create API Keys used to authenticate all requests to the API.
          <a
            rel="noopener"
            href="https://docs.inscribe.ai/reference/overview"
            target="_blank"
            class="i-link"
            tabindex="0"
            >Learn more</a
          >
        </p>
      </div>
      <i-button
        text="Create API Key"
        size="md"
        @button-click="redirectToCreateKey()"
      ></i-button>
    </div>
    <div v-if="currentUser" class="mx-0">
      <div class="justify-content-center">
        <div class="w-100">
          <!-- Empty State with no API Keys -->
          <empty-page
            v-if="!hasApiKeys"
            icon="large-password-key"
            title="Start by creating an API key"
            message="API keys are used to authenticate all requests to the API."
            link="https://docs.inscribe.ai/reference/overview"
            btn-text="Create API Key"
            :btn-click="redirectToCreateKey"
          ></empty-page>
          <!-- Table of Existing API Keys -->
          <div v-else class="d-flex flex-column w-100">
            <b-table
              id="api-key-table"
              :fields="apiFields"
              :show-empty="false"
              hover
              :items="apiKeys"
              class="i-table"
              selectable
              :per-page="perPage"
              :current-page="currentPage"
              :responsive="true"
              @row-clicked="rowClicked"
            >
              <template #cell(name)="{ item: apiKey }">
                <span class="i-text-body-2 i-text-neutral-120"></span>
                <router-link
                  class="i-text-subheading-2 i-text-neutral-120"
                  :to="{
                    name: apiKey.active
                      ? 'api-settings-edit-key'
                      : 'api-settings-view-key',
                    params: { api_key: apiKey.key },
                  }"
                >
                  <span v-if="apiKey.name">{{ apiKey.name }}</span>
                  <span v-else>{{ apiKey.key }}</span>
                </router-link>
              </template>
              <template #cell(last_used_at)="{ item: apiKey }">
                <div class="i-text-body-2 i-text-neutral-120">
                  <span v-if="apiKey.last_used_at">
                    {{ formatDateTime(apiKey.last_used_at, 'lll') }}
                  </span>
                  <span v-else-if="apiKey.just_added"> Just added </span>
                  <span v-else> Unused </span>
                </div>
              </template>
              <template #cell(active)="data">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="i-pill i-text-subheading-3">
                    <span v-if="data.item.active" class="success">Active</span>
                    <span v-else class="neutral">Deactivated</span>
                  </div>
                </div>
              </template>
              <template #cell(action)="data">
                <action-menu
                  :item="data.item"
                  :actions="actionButtons"
                  :bottom-row="bottomRow(data.index)"
                ></action-menu>
              </template>
            </b-table>
            <table-pagination
              table-id="api-key-table"
              :total-rows="keysLength"
              :per-page="perPage"
              :current-page="currentPage"
              @page-change="(page) => (currentPage = page)"
            >
            </table-pagination>
          </div>
        </div>
      </div>

      <b-modal
        v-if="!!apiKeyToDeactivate"
        centered
        :visible="!!apiKeyToDeactivate"
        modal-class="b-modal"
        @hidden="closeDeleteModal"
      >
        <template slot="modal-header">
          <div class="text-center">
            <h4 class="i-text-heading-4 i-text-neutral-120 i-mb-2">
              Deactivate API Key
            </h4>
            <h5 class="i-text-body-2 i-text-neutral-110 i-mb-0">
              Are you sure you want to deactivate this API Key? You cannot
              <!-- eslint-disable-next-line prettier/prettier -->
              undo this action.
            </h5>
          </div>
        </template>

        <div class="d-flex align-items-center justify-content-center">
          <i-button
            text="Cancel"
            type="neutral"
            subtype="subtle"
            size="lg"
            class="i-mr-6"
            @button-click="closeDeleteModal()"
          ></i-button>
          <i-button
            text="Deactivate"
            type="error"
            size="lg"
            @button-click="deactivateToken()"
          ></i-button>
        </div>
        <template slot="modal-footer"> &nbsp; </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import { iButton } from '@inscribe/signal';
import ActionMenu from '@/shared/components/ActionMenu';
import EmptyPage from '@/shared/components/EmptyPage.vue';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';
import TablePagination from '@/shared/components/TablePagination';
import { formatDateTime } from '@/utils/date';

export default {
  components: {
    'action-menu': ActionMenu,
    'empty-page': EmptyPage,
    'table-pagination': TablePagination,
    'i-button': iButton,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      learnMoreLink: '',
      perPage: 8,
      currentPage: 1,
      loading: false,
      newApiToken: null,
      apiKeyToDeactivate: null,
      apiFields: [
        { key: 'name', label: 'Name', sortable: true, thClass: 'name-column' },
        {
          key: 'last_used_at',
          label: 'Last Used',
          sortable: true,
          thClass: 'last-used-column',
        },
        {
          key: 'active',
          label: 'Status',
          sortable: true,
          thClass: 'status-column',
        },
        { key: 'action', label: '', thClass: 'action-column' },
      ],
      actionButtons: [
        {
          text: 'View',
          icon: 'small-edit-view',
          onClick: this.viewKey,
          isVisible: (apiKey) => !apiKey.active,
        },
        {
          text: 'Edit',
          icon: 'small-edit-pencil',
          onClick: this.editKey,
          isVisible: (apiKey) => apiKey.active,
        },
        {
          text: 'Deactivate',
          icon: 'small-delete-circle',
          destructive: true,
          onClick: this.setApiKeyToDeactivate,
          isVisible: (apiKey) => apiKey.active,
        },
      ],
    };
  },
  computed: {
    ...mapState(['currentUser']),

    apiKeys() {
      return this.currentUser.organisation.api_keys;
    },

    keysLength() {
      return this.apiKeys.length;
    },

    hasApiKeys() {
      return this.keysLength > 0;
    },

    showPagination() {
      return this.keysLength > this.perPage;
    },
  },
  methods: {
    formatDateTime,
    ...mapActions(['deactivateApiKey']),

    ...mapMutations(['changeApiKeyStatus']),

    bottomRow(rowindex) {
      return rowindex >= this.perPage - 2;
    },

    rowClicked(key) {
      key.active ? this.editKey(key) : this.viewKey(key);
    },

    editKey(data) {
      this.$router.push({
        name: 'api-settings-edit-key',
        params: { api_key: data.key },
      });
    },
    viewKey(data) {
      this.$router.push({
        name: 'api-settings-view-key',
        params: { api_key: data.key },
      });
    },

    redirectToCreateKey() {
      this.$router.push({
        name: 'api-settings-create-key',
      });
    },

    setApiKeyToDeactivate(data) {
      this.apiKeyToDeactivate = data.key;
    },

    deactivateToken() {
      this.deactivateApiKey(this.apiKeyToDeactivate).then((response) => {
        if (response.data.success) {
          this.$notify({
            group: 'api',
            type: 'success',
            duration: 5000,
            text: 'API Key deactivated',
          });

          this.changeApiKeyStatus(this.apiKeyToDeactivate);
          this.closeDeleteModal();
        } else {
          this.$notify({
            group: 'api',
            type: 'error',
            duration: 5000,
            text: `Could not delete API key, error: ${response.data.message}.`,
          });
        }
      });
    },

    closeDeleteModal() {
      this.apiKeyToDeactivate = null;
    },
  },
};
</script>

<style lang="scss" scoped>
$icon-size: 128px;

::v-deep .i-table {
  min-height: 300px;

  .name-column {
    width: auto;
  }
  .last-used-column {
    width: 220px;
  }
  .status-column {
    width: 120px;
  }
  .action-column {
    width: 40px;
  }
}

.no-api-keys {
  width: 100%;
  min-height: 600px;

  &__body {
    max-width: 300px;

    .icon-wrapper {
      width: $icon-size;
      height: $icon-size;
      border-radius: calc($icon-size / 2);
      background-color: $light-primary-20;
      .key-icon {
        color: $light-primary-110 !important;
      }
    }
  }
}
</style>
